import FacebookPixel from '@app-studio-web/ui/components/FacebookPixel'
import { AppConfigProvider } from '@app-studio-web/ui/contexts/AppConfigContext'
import { initConfidenceSessionRecording } from '@app-studio-web/ui/helpers/sessionRecording'
import ErrorPage from '@app-studio-web/ui/pages/ErrorPage'
import routes from '@pages/routes'
import * as Sentry from '@sentry/react'
import { newTracker } from '@snowplow/browser-tracker'
import { useEffect } from 'react'
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType,
} from 'react-router-dom'
import appScreenshot from './assets/screenshot.webp'
import Logo from './components/Logo'
import { markTrialFunnel } from './helpers/trialFunnel'
import { markVoFunnel } from './helpers/voFunnel'

if (import.meta.env.MODE !== 'development') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN as string,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        createRoutesFromChildren,
        matchRoutes,
        useEffect: useEffect,
        useLocation,
        useNavigationType,
      }),
    ],
    tracesSampleRate: 0, // NOTE: Disable performance monitoring.
  })

  // Shares the flag client secret: which sessions are recorded is decided by
  // the recording policy in Confidence, not here. Localhost is left out the
  // same way Sentry and Snowplow are, so development browsing does not turn up
  // in the recordings of a live test.
  initConfidenceSessionRecording(
    import.meta.env.VITE_CONFIDENCE_CLIENT_SECRET as string,
  )
}

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)

const router = sentryCreateBrowserRouter(routes)

function resolveFacebookPixelId(): string {
  const { pathname } = window.location

  // Sticky: once a visitor enters the trial funnel, remember it so later
  // come-back visits through the normal funnel still get the trial sales page.
  if (pathname.startsWith('/trial')) {
    markTrialFunnel()
  }

  // Sticky: same idea for the "vo" funnel, which has its own pricing page.
  if (pathname.startsWith('/vo')) {
    markVoFunnel()
  }

  return import.meta.env.VITE_FACEBOOK_PIXEL_ID as string
}

const facebookPixelId = resolveFacebookPixelId()

// Created here rather than from an effect. React flushes effects child-first,
// so a route that renders in the same commit as `App` fires its page view
// before `App`'s effect has run — and Snowplow drops calls made while no
// tracker is registered, taking the event with them. Routes whose loaders
// defer the first render happen to avoid that, which makes it a property of
// the route rather than of the tracking. Nothing here needs React, so it runs
// at import time and the ordering question disappears.
if (import.meta.env.MODE !== 'development') {
  newTracker('sp1', import.meta.env.VITE_SNOWPLOW_TRACKER_URL as string, {
    appId: import.meta.env.VITE_SNOWPLOW_APP_ID as string,
    platform: 'web',
  })
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorPage />}>
      <AppConfigProvider config={{ logo: <Logo />, appScreenshot }}>
        <FacebookPixel pixelId={facebookPixelId} />

        <RouterProvider router={router} />
      </AppConfigProvider>
    </Sentry.ErrorBoundary>
  )
}
